import React from "react";
import { Group, Rect, Image, Text } from "react-konva";
import { useImage } from "react-konva-utils";
import { useAtomValue } from "jotai";
import { isExportingAtom } from "state-atoms";

const IMAGE_CORNER_RADIUS = 10;

export const PreviewImage = React.memo(({ src, width, height }: { src: string; width: number; height: number }) => {
  const [image, loadState] = useImage(src);

  const isExportingCanvas = useAtomValue(isExportingAtom);
  const shouldRenderImage = !isExportingCanvas || !/http(s)?:\/\//.test(src);

  if (loadState === "failed" || !shouldRenderImage) {
    return (
      <Group>
        <Rect x={0} y={0} width={width} height={height} fill="#EBEDF3" cornerRadius={IMAGE_CORNER_RADIUS} />
        <Text
          y={height / 2 - 8}
          width={width}
          text={isExportingCanvas ? "Cannot export image" : "Failed to load image"}
          fontFamily="Poppins"
          fontSize={12}
          fill="#848199"
          align="center"
          lineHeight={1.3}
          letterSpacing={0.187}
        />
      </Group>
    );
  }

  if (loadState !== "loaded" || !image || !shouldRenderImage) {
    return <Rect x={0} y={0} width={width} height={height} fill="#EBEDF3" cornerRadius={IMAGE_CORNER_RADIUS} />;
  }

  const { imageWidth, imageHeight, imageX, imageY } = getContainDimensions(image, width, height);

  return (
    <Group
      x={imageX}
      y={imageY}
      clipFunc={(context: CanvasRenderingContext2D) => {
        const r = Math.min(IMAGE_CORNER_RADIUS, imageWidth / 2, imageHeight / 2);

        // Use roundRect if available (modern browsers), otherwise fall back to arcTo
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((context as any).roundRect) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (context as any).roundRect(0, 0, imageWidth, imageHeight, r);
        } else {
          context.beginPath();
          context.moveTo(r, 0);
          context.arcTo(imageWidth, 0, imageWidth, imageHeight, r);
          context.arcTo(imageWidth, imageHeight, 0, imageHeight, r);
          context.arcTo(0, imageHeight, 0, 0, r);
          context.arcTo(0, 0, imageWidth, 0, r);
          context.closePath();
        }
      }}
    >
      <Image image={image} x={0} y={0} width={imageWidth} height={imageHeight} />
    </Group>
  );
});

PreviewImage.displayName = "PreviewImage";

/**
 * Calculate dimensions and position to achieve object-fit: contain behavior
 * This ensures the entire image is visible, centered, while maintaining aspect ratio
 */
function getContainDimensions(
  image: HTMLImageElement,
  containerWidth: number,
  containerHeight: number
): { imageWidth: number; imageHeight: number; imageX: number; imageY: number } {
  const imageAspect = image.width / image.height;
  const containerAspect = containerWidth / containerHeight;

  let imageWidth: number;
  let imageHeight: number;

  if (imageAspect > containerAspect) {
    // Image is wider than container - fit to width
    imageWidth = containerWidth;
    imageHeight = containerWidth / imageAspect;
  } else {
    // Image is taller than container - fit to height
    imageHeight = containerHeight;
    imageWidth = containerHeight * imageAspect;
  }

  // Center the image
  const imageX = (containerWidth - imageWidth) / 2;
  const imageY = (containerHeight - imageHeight) / 2;

  return { imageWidth, imageHeight, imageX, imageY };
}
